import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Logo } from "../../components/Logo";
import { ArrowLeft, Plus, Save, Trash2, X, Upload } from "lucide-react";

const EMPTY = { slug: "", name: "", description: "", price: "", currency: "MUR", image: "", variants: [], category: "vetement", in_stock: true, order: 999 };

export default function AdminProducts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [variantInput, setVariantInput] = useState("");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => { if (user === false) navigate("/admin/login"); }, [user, navigate]);

  const load = () => {
    setLoading(true);
    api.get("/admin/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { if (user && (user.role === "admin" || user.role === "editor")) load(); }, [user]);

  const startEdit = (p) => { setEditing(p.slug); setForm({ ...EMPTY, ...p }); setError(""); };
  const startNew = () => { setEditing("new"); setForm(EMPTY); setError(""); };
  const cancel = () => { setEditing(null); setError(""); };

  const save = async () => {
    setError("");
    try {
      if (editing === "new") await api.post("/admin/products", form);
      else await api.put(`/admin/products/${editing}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Sauvegarde impossible");
    }
  };

  const del = async (slug) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    await api.delete(`/admin/products/${slug}`);
    load();
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.url }));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const addVariant = () => {
    const v = variantInput.trim();
    if (v && !form.variants.includes(v)) setForm((f) => ({ ...f, variants: [...f.variants, v] }));
    setVariantInput("");
  };

  if (!user || user === null) return <div className="py-32 text-center font-mono-label">CHARGEMENT…</div>;

  return (
    <div className="min-h-screen bg-[#FAF8F3]" data-testid="admin-products">
      <header className="border-b-2 border-[#1A1A18] bg-[#FAF8F3] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="font-mono-label flex items-center gap-1 hover:text-[#4A7A3D]" data-testid="products-back"><ArrowLeft size={14} /> Retour</Link>
            <Logo size="sm" />
          </div>
          <button onClick={startNew} className="bg-[#4A7A3D] text-[#FAF8F3] px-4 py-2 font-mono-label hover:bg-[#3E6633] flex items-center gap-2" data-testid="products-new">
            <Plus size={16} /> Nouveau produit
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
        <div>
          <div className="font-mono-label text-[#4A7A3D] mb-2">Gestion</div>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.0]">Boutique</h1>
        </div>

        {editing === "new" && (
          <ProductForm form={form} setForm={setForm} onSave={save} onCancel={cancel} error={error}
            upload={upload} uploading={uploading} variantInput={variantInput} setVariantInput={setVariantInput} addVariant={addVariant} isNew />
        )}

        {loading ? <p className="font-mono-label">CHARGEMENT…</p> : (
          <div className="space-y-4">
            {products.map((p) => (
              <div key={p.slug} className="border-2 border-[#E0D8CE] bg-[#FAF8F3]" data-testid={`product-row-${p.slug}`}>
                {editing === p.slug ? (
                  <div className="p-4 bg-[#F5EFEB]">
                    <ProductForm form={form} setForm={setForm} onSave={save} onCancel={cancel} error={error}
                      upload={upload} uploading={uploading} variantInput={variantInput} setVariantInput={setVariantInput} addVariant={addVariant} />
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-4 p-4 items-center">
                    <div className="w-20 h-20 bg-[#F5EFEB] flex-shrink-0">
                      {p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <div className="font-display text-xl leading-tight">{p.name}</div>
                      <div className="font-mono-label text-[#4A7A3D]">{p.price}</div>
                      <div className="text-xs text-[#4B4B48] mt-1">/{p.slug} · {p.variants?.length || 0} taille(s)</div>
                    </div>
                    <button onClick={() => startEdit(p)} className="border-2 border-[#1A1A18] px-3 py-1.5 font-mono-label hover:bg-[#1A1A18] hover:text-[#FAF8F3]" data-testid={`product-edit-${p.slug}`}>Éditer</button>
                    <button onClick={() => del(p.slug)} className="border-2 border-red-700 text-red-700 px-3 py-1.5 font-mono-label hover:bg-red-700 hover:text-white" data-testid={`product-delete-${p.slug}`}><Trash2 size={14} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function ProductForm({ form, setForm, onSave, onCancel, error, upload, uploading, variantInput, setVariantInput, addVariant }) {
  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="space-y-4 p-4 border-2 border-[#1A1A18] bg-[#F5EFEB]">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="font-mono-label text-[#4B4B48] block mb-1">Nom du produit</label>
          <input value={form.name} onChange={(e) => u("name", e.target.value)} className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 font-display text-xl outline-none" data-testid="product-form-name" />
        </div>
        <div>
          <label className="font-mono-label text-[#4B4B48] block mb-1">Prix (texte libre)</label>
          <input value={form.price} onChange={(e) => u("price", e.target.value)} placeholder="Rs 650" className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 outline-none" data-testid="product-form-price" />
        </div>
      </div>
      <div>
        <label className="font-mono-label text-[#4B4B48] block mb-1">Description</label>
        <textarea rows={3} value={form.description} onChange={(e) => u("description", e.target.value)} className="bg-transparent border-2 border-[#E0D8CE] w-full p-2 outline-none focus:border-[#4A7A3D]" data-testid="product-form-description" />
      </div>
      <div>
        <label className="font-mono-label text-[#4B4B48] block mb-1">Image</label>
        {form.image && <img src={form.image} alt="" className="max-h-40 object-cover mb-2 border" />}
        <div className="flex flex-wrap gap-2 items-center">
          <label className="border-2 border-[#1A1A18] px-3 py-2 font-mono-label cursor-pointer hover:bg-[#1A1A18] hover:text-[#FAF8F3] flex items-center gap-2">
            <Upload size={14} /> {uploading ? "Upload…" : "Uploader"}
            <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="product-form-upload" />
          </label>
          <input type="url" placeholder="ou URL" value={form.image} onChange={(e) => u("image", e.target.value)} className="bg-transparent border-b-2 border-[#1A1A18] flex-1 min-w-[200px] py-2 outline-none" data-testid="product-form-image-url" />
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className="font-mono-label text-[#4B4B48] block mb-1">Catégorie</label>
          <select value={form.category} onChange={(e) => u("category", e.target.value)} className="bg-transparent border-2 border-[#1A1A18] w-full p-2 font-mono-label" data-testid="product-form-category">
            <option value="vetement">Vêtement</option>
            <option value="accessoire">Accessoire</option>
            <option value="autre">Autre</option>
          </select>
        </div>
        <div>
          <label className="font-mono-label text-[#4B4B48] block mb-1">Stock</label>
          <label className="flex items-center gap-2 mt-2 font-mono-label">
            <input type="checkbox" checked={form.in_stock} onChange={(e) => u("in_stock", e.target.checked)} className="w-5 h-5" data-testid="product-form-stock" />
            Disponible
          </label>
        </div>
      </div>
      <div>
        <label className="font-mono-label text-[#4B4B48] block mb-1">Tailles / Variantes</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {form.variants.map((v) => (
            <span key={v} className="font-mono-label bg-[#FAF8F3] border border-[#E0D8CE] px-3 py-1 flex items-center gap-2">
              {v}
              <button onClick={() => u("variants", form.variants.filter((x) => x !== v))}><X size={12} /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={variantInput} onChange={(e) => setVariantInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addVariant(); } }} placeholder="S, M, L…" className="bg-transparent border-b-2 border-[#1A1A18] flex-1 py-2 outline-none" data-testid="product-form-variant-input" />
          <button onClick={addVariant} className="border-2 border-[#1A1A18] px-4 py-2 font-mono-label" data-testid="product-form-variant-add">+</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 justify-end">
        {error && <div className="text-red-700 font-mono-label flex-1">{error}</div>}
        <button onClick={onCancel} className="border-2 border-[#1A1A18] px-4 py-2 font-mono-label flex items-center gap-2" data-testid="product-form-cancel"><X size={14} /> Annuler</button>
        <button onClick={onSave} className="bg-[#4A7A3D] text-[#FAF8F3] px-4 py-2 font-mono-label flex items-center gap-2 hover:bg-[#3E6633]" data-testid="product-form-save"><Save size={14} /> Sauvegarder</button>
      </div>
    </div>
  );
}
